import Add from "@mui/icons-material/Add";
import Delete from "@mui/icons-material/Delete";
import { Box, Button, FormControlLabel, Switch, Tooltip } from "@mui/material";
import { GridActionsCellItem, GridRowParams, GridSortModel } from "@mui/x-data-grid-premium";
import { SkillsQuery, SkillsQueryVariables } from "generated-graphql/graphql";
import { OmnisearchDataGridV2, useLocalOmnisearch } from "hooks/omnisearch-v2";
import { DataGridSkeleton } from "hooks/omnisearch-v2/DataGridSkeleton";
import { FilterKey, OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useCallback, useMemo } from "react";

import { SKILLS_QUERY } from "./skillQueries";
import { SKILLS_GRID_COLUMNS, type SkillRow } from "./skillsGridColumns";

/** Fixed page size; a single option hides the size picker. */
const PAGE_SIZE = 5;
const PAGE_SIZE_OPTIONS = [PAGE_SIZE];
const INITIAL_SORT_MODEL: GridSortModel = [{ field: "updatedAt", sort: "desc" }];
/** Two-line name cell (title + description) needs a bit more than the default 38. */
const ROW_HEIGHT = 52;
/**
 * Pinned to a full page (header + rows + footer + borders) so the dialog doesn't
 * resize on a short page or between the skeleton and the loaded grid.
 */
const GRID_HEIGHT = 56 + PAGE_SIZE * ROW_HEIGHT + 52 + 3;
/** Clears the grid's 300px page-layout minimums, which would overflow this height. */
const GRID_SX = {
  height: GRID_HEIGHT,
  "& .MuiDataGrid-virtualScroller, & .MuiDataGrid-overlayWrapper, & .MuiDataGrid-overlayWrapperInner":
    { minHeight: 0 },
};
const OWNED_BY_ME_KEY = "ownedByMe";
/** Filter menu only — disabled state shows as a chip on the name cell, not a column. */
const ADDITIONAL_FILTER_KEYS: FilterKey[] = [
  { header: "Enabled", key: "enabled", filterKeyType: "boolean" },
];

const getItems = (data: SkillsQuery) => data.skills.items;
const getCount = (data: SkillsQuery) => data.skills.count;

const GRID_FALLBACK = (
  <DataGridSkeleton
    rows={PAGE_SIZE}
    rowHeight={ROW_HEIGHT}
    columnFlexes={[1.6, 0.4, 0.5, 0.4, 0.1]}
  />
);

type SkillsListGridProps = {
  tenantId: string | undefined;
  open: boolean;
  onAddSkill: () => void;
  onRowClick: (params: GridRowParams<SkillRow>) => void;
  onDeleteSkill: (skill: SkillRow) => void;
  canDeleteSkill: (skill: SkillRow) => boolean;
  deleting?: boolean;
};

export function SkillsListGrid({
  tenantId,
  open,
  onAddSkill,
  onRowClick,
  onDeleteSkill,
  canDeleteSkill,
  deleting = false,
}: SkillsListGridProps) {
  const omnisearchSource = useLocalOmnisearch();
  const ownedByMe = omnisearchSource.getOmnisearchValue(OWNED_BY_ME_KEY)?.toLowerCase() === "true";

  const buildVariables = useCallback(
    (
      omnisearch: string,
      pagination: { page: number; pageSize: number },
      sort: GridSortModel
    ): SkillsQueryVariables => ({
      tenantId: tenantId ?? "",
      search: omnisearch || undefined,
      // Omit `enabled` so drafts (enabled: false) appear alongside live skills.
      page: pagination.page,
      pageSize: pagination.pageSize,
      sort: sort.map((s) => ({ field: s.field, sort: s.sort })),
    }),
    [tenantId]
  );

  const columns = useMemo((): OmnisearchGridColDef<SkillRow>[] => {
    return [
      ...SKILLS_GRID_COLUMNS,
      {
        field: "actions",
        type: "actions",
        width: 56,
        sortable: false,
        filterable: false,
        getActions: (params) => {
          if (!canDeleteSkill(params.row)) {
            return [];
          }
          return [
            <Tooltip title="Delete skill" key="delete">
              <GridActionsCellItem
                icon={<Delete />}
                label="Delete skill"
                onClick={(event) => {
                  event.stopPropagation();
                  onDeleteSkill(params.row);
                }}
                disabled={deleting}
              />
            </Tooltip>,
          ];
        },
      },
    ];
  }, [canDeleteSkill, deleting, onDeleteSkill]);

  const commandButtons = useMemo(
    () => [
      <FormControlLabel
        key="owned-by-me"
        control={
          <Switch
            size="small"
            checked={ownedByMe}
            onChange={(_, checked) => {
              if (checked) {
                omnisearchSource.editOmnisearchKeyValues([[OWNED_BY_ME_KEY, "true"]]);
              } else {
                omnisearchSource.removeOmnisearchKeyValue(OWNED_BY_ME_KEY);
              }
            }}
            inputProps={{ "aria-label": "Owned by me" }}
          />
        }
        label="Owned by me"
        sx={{ mr: 1, ml: 0, typography: "body2" }}
      />,
      <Button
        key="add-skill"
        variant="outlined"
        size="small"
        startIcon={<Add />}
        onClick={onAddSkill}
      >
        Add skill
      </Button>,
    ],
    [onAddSkill, omnisearchSource, ownedByMe]
  );

  return (
    <Box sx={{ width: "100%" }}>
      <OmnisearchDataGridV2
        omnisearchSource={omnisearchSource}
        persistenceKey="scout-manage-skills-page5"
        query={SKILLS_QUERY}
        getItems={getItems}
        getCount={getCount}
        columns={columns}
        additionalFilterKeys={ADDITIONAL_FILTER_KEYS}
        buildVariables={buildVariables}
        commandButtons={commandButtons}
        onRowClick={onRowClick}
        initialSortModel={INITIAL_SORT_MODEL}
        initialPageSize={PAGE_SIZE}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        tenantId={tenantId}
        skip={!open || !tenantId}
        shouldShowManageColumns={false}
        noRowsMessage="No skills yet. Add one to get started."
        rowHeight={ROW_HEIGHT}
        // Always show "1–5 of N" so the height doesn't depend on how many skills exist.
        hideFooter={false}
        gridSx={GRID_SX}
        compact
        fallback={GRID_FALLBACK}
      />
    </Box>
  );
}
